import React from 'react';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    price: 79,
    audience: 'For teams comfortable installing the widget themselves',
    summary: 'A practical product-discovery and support assistant, with a lightweight monthly review to guide the next improvement.',
    features: [
      'Self-serve widget installation after preview',
      'Answers grounded in agreed product and support content',
      'Monthly lightweight performance snapshot based on available usage and question data',
      'One prioritized improvement recommendation each month',
      'Standard support for setup questions',
    ],
    note: 'Best for a straightforward first deployment.',
    href: '/trial?plan=starter',
    cta: 'Start Starter trial',
  },
  {
    key: 'growth',
    name: 'Growth',
    price: 299,
    audience: 'For teams that want help getting live and improving it',
    summary: 'A guided launch plus hands-on monthly improvements informed by unanswered questions and content gaps.',
    features: [
      'Guided onboarding and content setup',
      'Done-for-you widget installation assistance and QA',
      'Review of unanswered questions and content gaps',
      'One hands-on monthly improvement pass for answer quality and content gaps',
      'Support-workflow and escalation handoff review',
    ],
    note: 'Best fit when you want a more hands-on launch.',
    href: '/trial?plan=growth',
    cta: 'Start Growth trial',
    featured: true,
  },
  {
    key: 'pro',
    name: 'Pro',
    price: 999,
    audience: 'For larger stores or more involved requirements',
    summary: 'A higher-touch engagement for broader knowledge coverage, customized reporting, and ongoing optimization.',
    features: [
      'Broader catalog or knowledge-base ingestion, scoped to your content',
      'Integration and connector planning where supported',
      'Customized reporting and performance reviews, scoped to agreed data',
      'Priority support and optimization, with scope agreed upfront',
    ],
    note: 'Specific integrations and deliverables are confirmed before work begins.',
    href: 'mailto:hello@scalabledeploy.com?subject=ScalableDeploy%20Pro%20inquiry',
    cta: 'Discuss Pro requirements',
    salesAssisted: true,
  },
];

function PlanCard({ plan }) {
  const cardClass = plan.featured
    ? 'border-indigo-300 bg-indigo-50 shadow-md ring-1 ring-indigo-200'
    : 'border-gray-200 bg-white shadow-sm';
  const ctaClass = plan.featured
    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
    : 'border border-gray-300 text-gray-800 hover:bg-gray-50';

  return (
    <article className={`relative flex h-full flex-col rounded-xl border p-5 ${cardClass}`}>
      {plan.featured && (
        <div className="absolute right-4 top-4 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-800">
          Most hands-on
        </div>
      )}
      <div className="text-sm font-semibold text-gray-500">{plan.name}{plan.salesAssisted ? ' · sales-assisted' : ''}</div>
      <div className="mt-2 text-3xl font-bold tracking-tight">
        ${plan.price}<span className="text-sm font-normal text-gray-500">/month</span>
      </div>
      <p className="mt-3 text-sm font-medium text-gray-800">{plan.audience}</p>
      <p className="mt-2 min-h-[3rem] text-sm text-gray-600">{plan.summary}</p>
      <div className="mt-4 border-t border-gray-200 pt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Included</div>
        <ul className="space-y-2 text-sm text-gray-700">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <span aria-hidden="true" className="mt-0.5 font-bold text-indigo-600">✓</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-gray-500">{plan.note}</p>
      <div className="mt-auto pt-5">
        {plan.salesAssisted ? (
          <a href={plan.href} className={`block rounded-lg px-4 py-2.5 text-center text-sm font-medium ${ctaClass}`}>
            {plan.cta}
          </a>
        ) : (
          <Link to={plan.href} className={`block rounded-lg px-4 py-2.5 text-center text-sm font-medium ${ctaClass}`}>
            {plan.cta}
          </Link>
        )}
      </div>
    </article>
  );
}

export default function LandingPage() {
  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* HERO */}
      <header className="mb-12 text-center">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-800">
          <span className="font-semibold">7-day trial:</span>
          private preview first · usually ~48 hours · install choice · trial snapshot
        </div>

        <h1 className="mb-4 text-3xl font-bold tracking-tight sm:text-5xl">
          Turn your product knowledge into a grounded AI search + support experience.
        </h1>

        <p className="mx-auto mb-6 max-w-3xl text-lg text-gray-700">
          Help shoppers find the right products, get answers from your store content, and navigate common support questions.
          We prepare a private preview first—usually in about 48 hours—so you can test real questions before installing it.
        </p>

        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/trial?plan=growth" className="rounded-lg bg-indigo-600 px-6 py-3 font-medium text-white shadow-md hover:bg-indigo-700">
            Start 7-day trial
          </Link>
          <Link to="/demo/rag?mode=dtc_supplements" className="rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-800 hover:bg-gray-50">
            View live demo
          </Link>
          <Link to="/trial?plan=starter" className="px-6 py-3 font-medium text-indigo-600 hover:underline">
            Try Starter instead
          </Link>
        </div>

        <p className="mx-auto mt-4 max-w-2xl text-xs text-gray-500">
          A card is required to activate the trial. Your preview is prepared before installation. After the 7-day trial,
          the selected plan starts billing monthly unless you cancel beforehand.
        </p>
      </header>

      {/* VALUE / DIFFERENTIATION */}
      <section className="mb-12">
        <div className="mb-5 text-center">
          <h2 className="mb-2 text-2xl font-semibold">Not another generic chatbot.</h2>
          <p className="mx-auto max-w-2xl text-sm text-gray-600">
            ScalableDeploy is centered on the information customers need to make product decisions and get help—not just open-ended conversation.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h3 className="mb-2 font-semibold">Grounded in your knowledge</h3>
            <p className="text-gray-600">Use agreed product pages, FAQs, policies, support content, and educational material as the basis for answers.</p>
          </div>
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h3 className="mb-2 font-semibold">Built for product decisions</h3>
            <p className="text-gray-600">Help shoppers compare options, understand product details, and find a better fit before purchasing.</p>
          </div>
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h3 className="mb-2 font-semibold">Useful for common support questions</h3>
            <p className="text-gray-600">Answer supported policy questions from store content. Order lookup and human handoff depend on the integrations configured for each client.</p>
          </div>
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5">
            <h3 className="mb-2 font-semibold">Validate before you install</h3>
            <p className="text-gray-700">Test a private preview first, review question patterns and content gaps, then decide whether to continue.</p>
          </div>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="mb-12 rounded-xl border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-2xl font-semibold">Who this is for</h2>
        <div className="grid gap-4 text-sm text-gray-700 md:grid-cols-3">
          <div>
            <h3 className="mb-1 font-semibold">DTC brands</h3>
            <p>Supplements, beauty, wellness, food, apparel, and consumer brands with repeated product and support questions.</p>
          </div>
          <div>
            <h3 className="mb-1 font-semibold">Ecommerce teams</h3>
            <p>Stores that want clearer answers across product details, FAQs, shipping, returns, subscriptions, and support policies.</p>
          </div>
          <div>
            <h3 className="mb-1 font-semibold">Agencies</h3>
            <p>Teams exploring a branded AI assistant with a defined preview, installation, and ongoing improvement path.</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mb-12 rounded-xl border-l-4 border-indigo-600 bg-indigo-50 p-6">
        <h2 className="mb-3 text-2xl font-semibold">How the trial works</h2>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
          <li><strong>Activate the trial:</strong> enter your work email and store URL, then confirm your card.</li>
          <li><strong>We prepare the workspace:</strong> curate agreed source content and configure the assistant.</li>
          <li><strong>Test a private preview:</strong> the preview is usually ready in about 48 hours, after setup and QA.</li>
          <li><strong>Choose your install path:</strong> follow the self-serve instructions or request done-for-you installation assistance.</li>
          <li><strong>Review the trial snapshot:</strong> use observed questions, answer-quality notes, and content gaps to decide the next step.</li>
        </ol>
      </section>

      {/* TRIAL SNAPSHOT */}
      <section className="mb-12 rounded-xl border bg-white p-6 shadow-sm">
        <div className="max-w-3xl">
          <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-indigo-600">Included with the trial</div>
          <h2 className="mb-3 text-2xl font-semibold">A useful snapshot before you continue</h2>
          <p className="mb-4 text-sm text-gray-700">
            Near the end of the trial, we prepare a short review of what was observed and whether the assistant appears worth continuing, tuning, or stopping. Available metrics depend on what is instrumented and recorded during the trial.
          </p>
          <div className="grid gap-4 text-sm text-gray-700 md:grid-cols-2">
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-2 font-semibold">What it may cover</h3>
              <ul className="list-disc space-y-1 pl-5">
                <li>Questions asked and common intent patterns</li>
                <li>Product, policy, or support topics that came up</li>
                <li>Fallbacks and content gaps identified during review</li>
                <li>Any available usage signals from the trial</li>
              </ul>
            </div>
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-2 font-semibold">How it helps</h3>
              <ul className="list-disc space-y-1 pl-5">
                <li>Answer-quality observations</li>
                <li>Practical opportunities to improve support or product discovery</li>
                <li>Recommended tuning priorities</li>
                <li>A clear next step: stop, tune, install, or continue</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INSTALL OPTIONS */}
      <section className="mb-12">
        <h2 className="mb-5 text-2xl font-semibold">Choose your install path after preview</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <div className="mb-1 text-sm text-gray-500">Done-for-you install assistance</div>
            <h3 className="mb-2 text-xl font-semibold">Get help getting it live</h3>
            <ul className="mb-5 list-disc space-y-1 pl-5 text-sm text-gray-700">
              <li>Guidance for a hands-off launch</li>
              <li>Widget installation assistance and QA</li>
              <li>Review of how the assistant fits your support process</li>
              <li>Best aligned with Growth</li>
            </ul>
            <Link to="/trial?plan=growth" className="block rounded-lg bg-indigo-600 px-4 py-2 text-center text-white hover:bg-indigo-700">Start Growth trial</Link>
          </div>
          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <div className="mb-1 text-sm text-gray-500">Self-serve install</div>
            <h3 className="mb-2 text-xl font-semibold">Use the install instructions</h3>
            <ul className="mb-5 list-disc space-y-1 pl-5 text-sm text-gray-700">
              <li>Best if you are comfortable adding a widget script</li>
              <li>Lower-touch path for initial validation</li>
              <li>Preview is prepared before you install</li>
              <li>Best aligned with Starter</li>
            </ul>
            <Link to="/trial?plan=starter" className="block rounded-lg border px-4 py-2 text-center hover:bg-gray-50">Start Starter trial</Link>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="mb-12" id="pricing">
        <div className="mb-6 text-center">
          <h2 className="mb-2 text-2xl font-semibold">Plans that scale with the help you need</h2>
          <p className="mx-auto max-w-3xl text-sm text-gray-600">
            All plans center on an assistant grounded in agreed store content. Starter includes a lightweight monthly performance snapshot and one prioritized recommendation; Growth adds hands-on implementation of an improvement; Pro adds customized reporting and broader optimization. Snapshot detail depends on available recorded data. Monthly billing begins after the 7-day trial unless you cancel before it ends.
          </p>
        </div>
        <div className="grid items-stretch gap-4 md:grid-cols-3">
          {PLANS.map((plan) => <PlanCard key={plan.key} plan={plan} />)}
        </div>
        <p className="mt-4 text-xs leading-relaxed text-gray-500">
          Usage allowances, third-party costs, integration availability, and exact Pro scope should be confirmed before purchase where applicable. Human handoff and order lookup require a supported, configured workflow; they are not automatically included as live integrations on every store.
        </p>
      </section>

      {/* FAQ */}
      <section className="mb-12 rounded-xl border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-2xl font-semibold">Common questions</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h3 className="mb-1 font-semibold">Do I install before seeing it?</h3>
            <p className="text-sm text-gray-600">No. We prepare a private preview first. You can test it before choosing a self-serve or done-for-you install path.</p>
          </div>
          <div>
            <h3 className="mb-1 font-semibold">When does billing start?</h3>
            <p className="text-sm text-gray-600">A card is required to activate the 7-day trial. Your selected monthly subscription starts when the trial ends unless you cancel beforehand.</p>
          </div>
          <div>
            <h3 className="mb-1 font-semibold">Does every store get order lookup or live human handoff?</h3>
            <p className="text-sm text-gray-600">Those workflows depend on available integrations and configuration. We confirm the supported scope before presenting them as live.</p>
          </div>
          <div>
            <h3 className="mb-1 font-semibold">Which plan should I choose?</h3>
            <p className="text-sm text-gray-600">Choose Starter if you can install it yourself, Growth if you want hands-on setup, or contact us about Pro if you have broader integration or reporting needs.</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="text-center">
        <h3 className="mb-2 text-xl font-semibold">Validate the assistant before installing it.</h3>
        <p className="mb-5 text-sm text-gray-700">Start with a private preview, test real customer questions, then choose the level of support that fits.</p>
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/trial?plan=growth" className="rounded-lg bg-indigo-600 px-6 py-3 text-white hover:bg-indigo-700">Start 7-day trial</Link>
          <Link to="/demo/rag?mode=dtc_supplements" className="rounded-lg border px-6 py-3 hover:bg-gray-50">Preview demo first</Link>
          <Link to="/trial?plan=starter" className="px-6 py-3 text-indigo-600 hover:underline">Start Starter trial</Link>
        </div>
      </section>
    </div>
  );
}
